/**
 * Poster Templates Service
 * 
 * DATABASE STORAGE (PosterTemplateDbRow):
 * - Stores `variants: PosterTemplateVariant[]` (required, NOT NULL)
 * - Each variant has: canvas_width, canvas_height, elements
 * 
 * LIBRARY EXPECTATION (PosterTemplate):
 * - Only has: id, title, description, variants (required)
 * - No legacy fields - variants-only approach
 * - Categories and tags are stored as extended properties in database rows
 * 
 * RENDERING:
 * - Use renderPosterVariant(variant, dataMap) to render a specific variant
 * - To render multiple variants, call renderPosterVariant for each variant
 * - UI components handle carousel by rendering each variant separately
 */

import { supabase } from '@/lib/supabase';
import { logger } from '@/lib/logger';
import { PosterTemplate, PosterTemplateVariant } from '@/libs/poster-generator/src/types';

/**
 * Database representation of a poster template
 * - Stores variants array (required) - each variant has canvas_width, canvas_height, elements
 * - No legacy canvas_width/canvas_height/elements at root level
 */
export interface PosterTemplateDbRow {
    id: string;
    template_id: string;
    title: string;
    description: string;
    categories?: string[]; // Fixed top-level categories (e.g., ['Festival', 'Religious'])
    tags?: string[]; // Dynamic, specific tags (e.g., ['Diwali', 'New Year'])
    target_store_types?: string[];
    variants: PosterTemplateVariant[]; // Required: at least one variant must exist
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

/**
 * Response from fetching multiple poster templates with pagination
 */
export interface PosterTemplatesListResponse {
    templates: PosterTemplate[];
    hasMore: boolean;
    nextCursor?: string;
}

/**
 * Convert database row to PosterTemplate format
 * Direct mapping - no legacy fields, variants-only
 * Preserves categories, tags, and target_store_types as extended properties for UI components
 */
function convertDbRowToLibTemplate(row: PosterTemplateDbRow): PosterTemplate & { categories?: string[]; tags?: string[]; target_store_types?: string[]; created_at?: string } {
    // Variants are required in DB
    if (!row.variants || row.variants.length === 0) {
        throw new Error(`Template ${row.template_id} has no variants`);
    }
    
    const template: PosterTemplate & { categories?: string[]; tags?: string[]; target_store_types?: string[]; created_at?: string } = {
        id: row.template_id,
        title: row.title,
        description: row.description,
        variants: row.variants
    };
    
    // Preserve categories for UI components (extended property) if they exist
    if (row.categories && row.categories.length > 0) {
        template.categories = row.categories;
    }
    
    // Preserve tags for UI components (extended property) if they exist
    if (row.tags && row.tags.length > 0) {
        template.tags = row.tags;
    }
    
    // Preserve target_store_types for UI components (extended property) if they exist
    if (row.target_store_types && row.target_store_types.length > 0) {
        template.target_store_types = row.target_store_types;
    }
    
    // Preserve created_at for filtering (extended property)
    if (row.created_at) {
        template.created_at = row.created_at;
    }
    
    return template;
}

/**
 * Fetch poster templates with pagination
 */
export async function fetchPosterTemplates(
    limit: number = 10,
    cursor?: string
): Promise<PosterTemplatesListResponse> {
    try {
        let query = supabase
            .from('poster_templates')
            .select('*')
            .eq('is_active', true)
            .order('created_at', { ascending: false })
            .limit(limit + 1); // Fetch one extra to check if there are more

        // If cursor is provided, fetch templates created before this timestamp
        if (cursor) {
            query = query.lt('created_at', cursor);
        }

        const { data, error } = await query;

        if (error) {
            logger.error('Error fetching poster templates', { error, component: 'PosterTemplatesService' });
            throw error;
        }

        if (!data) {
            return { templates: [], hasMore: false };
        }

        // Check if there are more templates
        const hasMore = data.length > limit;
        const templates = data.slice(0, limit).map(convertDbRowToLibTemplate);

        // Get the cursor for the next page (created_at of the last template)
        const nextCursor = hasMore && data.length > 0
            ? data[data.length - 1].created_at || undefined
            : undefined;

        return {
            templates,
            hasMore,
            nextCursor
        };
    } catch (error) {
        logger.error('Error in fetchPosterTemplates', { error, component: 'PosterTemplatesService' });
        throw error;
    }
}

/**
 * Fetch a single poster template by ID
 */
export async function fetchPosterTemplateById(templateId: string): Promise<PosterTemplate | null> {
    try {
        const { data, error } = await supabase
            .from('poster_templates')
            .select('*')
            .eq('template_id', templateId)
            .eq('is_active', true)
            .single();

        if (error) {
            logger.error('Error fetching poster template', { error, component: 'PosterTemplatesService' });
            return null;
        }

        return convertDbRowToLibTemplate(data);
    } catch (error) {
        logger.error('Error in fetchPosterTemplateById', { error, component: 'PosterTemplatesService' });
        return null;
    }
}

/**
 * Get all variants from a template
 * Always returns variants array (required field in database)
 */
export function getTemplateVariants(template: PosterTemplate): PosterTemplateVariant[] {
    // Variants are required - throw error if missing
    if (!template.variants || template.variants.length === 0) {
        throw new Error(`Template ${template.id} has no variants`);
    }
    return template.variants;
}

/**
 * Get unique tags and store types from normalized tables
 * Much more efficient than querying all templates
 */
export async function getTemplateFilters(): Promise<{ tags: string[]; storeTypes: string[] }> {
    try {
        // Fetch both in parallel for better performance
        const [tagsResult, storeTypesResult] = await Promise.all([
            supabase
                .from('poster_tags')
                .select('name')
                .order('name'),
            supabase
                .from('poster_store_types')
                .select('name')
                .order('name')
        ]);

        if (tagsResult.error) {
            logger.error('Error fetching poster tags', { error: tagsResult.error, component: 'PosterTemplatesService' });
            throw tagsResult.error;
        }

        if (storeTypesResult.error) {
            logger.error('Error fetching poster store types', { error: storeTypesResult.error, component: 'PosterTemplatesService' });
            throw storeTypesResult.error;
        }

        return {
            tags: (tagsResult.data || []).map(tag => tag.name),
            storeTypes: (storeTypesResult.data || []).map(type => type.name)
        };
    } catch (error) {
        logger.error('Error in getTemplateFilters', { error, component: 'PosterTemplatesService' });
        throw error;
    }
}

/**
 * Helper function to ensure tags exist in poster_tags table
 * Inserts tags if they don't exist (idempotent)
 */
export async function ensurePosterTags(tags: string[]): Promise<void> {
    if (!tags || tags.length === 0) return;

    try {
        // Insert tags that don't exist yet
        const insertData = tags.map(tag => ({ name: tag.trim() })).filter(tag => tag.name);
        
        if (insertData.length > 0) {
            const { error } = await supabase
                .from('poster_tags')
                .upsert(insertData, { onConflict: 'name', ignoreDuplicates: true });

            if (error) {
                logger.error('Error ensuring poster tags', { error, component: 'PosterTemplatesService' });
                // Don't throw - this is a helper function, continue even if it fails
            }
        }
    } catch (error) {
        logger.error('Error in ensurePosterTags', { error, component: 'PosterTemplatesService' });
        // Don't throw - continue execution
    }
}

/**
 * Helper function to ensure store types exist in poster_store_types table
 * Inserts store types if they don't exist (idempotent)
 * Excludes 'all' as it's not a real store type
 */
export async function ensurePosterStoreTypes(storeTypes: string[]): Promise<void> {
    if (!storeTypes || storeTypes.length === 0) return;

    try {
        // Filter out 'all' and empty strings
        const validStoreTypes = storeTypes
            .map(type => type.trim())
            .filter(type => type && type.toLowerCase() !== 'all');

        if (validStoreTypes.length === 0) return;

        const insertData = validStoreTypes.map(type => ({ name: type }));
        
        const { error } = await supabase
            .from('poster_store_types')
            .upsert(insertData, { onConflict: 'name', ignoreDuplicates: true });

        if (error) {
            logger.error('Error ensuring poster store types', { error, component: 'PosterTemplatesService' });
            // Don't throw - this is a helper function, continue even if it fails
        }
    } catch (error) {
        logger.error('Error in ensurePosterStoreTypes', { error, component: 'PosterTemplatesService' });
        // Don't throw - continue execution
    }
}

/**
 * Get unique tags from all active poster templates (from tags array column)
 * @deprecated Use getTemplateFilters() instead for better performance
 */
export async function getTemplateTags(): Promise<string[]> {
    const { tags } = await getTemplateFilters();
    return tags;
}

/**
 * Get unique store types from all active poster templates (from target_store_types array column)
 * @deprecated Use getTemplateFilters() instead for better performance
 */
export async function getTemplateStoreTypes(): Promise<string[]> {
    const { storeTypes } = await getTemplateFilters();
    return storeTypes;
}

/**
 * Fetch poster templates with filters for tag and store type
 */
export async function fetchPosterTemplatesWithFilters(
    tag: string | undefined,
    storeType: string | undefined,
    limit: number = 10,
    cursor?: string
): Promise<PosterTemplatesListResponse> {
    try {
        let query = supabase
            .from('poster_templates')
            .select('*')
            .eq('is_active', true)
            .order('created_at', { ascending: false })
            .limit(limit + 1);

        // Filter by tag if provided (check if tags array contains the tag)
        // Skip filter if tag is "All", undefined, or empty
        if (tag && tag !== 'All' && tag.trim() !== '') {
            query = query.contains('tags', [tag]);
        }

        // Filter by store type if provided
        // Include templates that either:
        // 1. Have the specific store type in target_store_types, OR
        // 2. Have "All Stores" in target_store_types (universal posters)
        if (storeType && storeType !== 'All Stores') {
            // Use PostgREST filter syntax: check if array contains storeType OR contains "All Stores"
            // Format: column.cs.{value} means "column contains value"
            // For OR conditions, comma-separate them
            const filterString = `target_store_types.cs.{${storeType}},target_store_types.cs.{"All Stores"}`;
            query = query.or(filterString);
        }

        if (cursor) {
            query = query.lt('created_at', cursor);
        }

        const { data, error } = await query;

        if (error) {
            logger.error('Error fetching poster templates with filters', { error, component: 'PosterTemplatesService' });
            throw error;
        }

        if (!data) {
            return { templates: [], hasMore: false };
        }

        const hasMore = data.length > limit;
        const templates = data.slice(0, limit).map(convertDbRowToLibTemplate);

        const nextCursor = hasMore && data.length > 0
            ? data[data.length - 1].created_at || undefined
            : undefined;

        return {
            templates,
            hasMore,
            nextCursor
        };
    } catch (error) {
        logger.error('Error in fetchPosterTemplatesWithFilters', { error, component: 'PosterTemplatesService' });
        throw error;
    }
}